<script setup lang="ts">
  import { z } from 'zod';
  import { UserWorkExperience } from '@borg/types';
  import { MpDatePicker, MpInput, MpTextArea, pushToastMessage } from '@borg/ui';
  import { EditModalEvents } from './types';
  import { getWorkExperienceModalTranslationKeys } from './utils';

  const props = defineProps<{
    isOpen: boolean;
    isVolunteering: boolean;
    id?: string;
  }>();

  const emits = defineEmits<EditModalEvents>();

  const { t } = useI18n();
  const inputRef = ref<InstanceType<typeof MpInput> | null>(null);

  const { values, state, validate, resetStates, setValues } = useForm({
    initialData: () => transformEntity(),
    schema: z.object({
      position: z.string().min(1, t('REQUIRED_INPUT')),
      companyName: z.string().min(1, t('REQUIRED_INPUT')),
      location: z.string().min(1, t('REQUIRED_INPUT')),
      startedOn: z.date({ invalid_type_error: t('REQUIRED_INPUT') }),
      endedOn: z.date().nullable(),
      description: z.string(),
    }),
  });

  const submitInProgress = ref(false);
  const getInProgress = ref(false);
  const isAwaitingResponse = computed(() => submitInProgress.value || getInProgress.value);

  const TRANSLATION_KEYS = getWorkExperienceModalTranslationKeys(props.isVolunteering);

  function transformEntity(input?: UserWorkExperience) {
    return {
      position: input?.position ?? '',
      companyName: input?.companyName ?? '',
      location: input?.location ?? '',
      description: input?.description ?? '',
      startedOn: toDate(input?.startedOn),
      endedOn: toDate(input?.endedOn),
    };
  }

  async function onModalOpened() {
    if (props.id) {
      getInProgress.value = true;
      const { data: workExperience, status } = await workExperienceRepository.get(props.id);
      if (status.value === 'success' && workExperience.value) {
        setValues(transformEntity(workExperience.value));
        getInProgress.value = false;
      } else {
        pushToastMessage({
          type: 'alert',
          subtitle: t('MY_PROFILE_PAGE.FETCH_ERROR_TOAST.SUBTITLE'),
        });
        onClose();
      }
    }
  }

  async function onSubmit() {
    const valid = await validate();
    if (valid) {
      const _values = {
        ...values,
        startedOn: getLocaleDate(values.startedOn),
        endedOn: getLocaleDate(values.endedOn),
      };
      try {
        submitInProgress.value = true;
        props.id
          ? await workExperienceRepository.save({ ..._values, id: props.id })
          : await workExperienceRepository.create({
              ..._values,
              isVolunteering: props.isVolunteering,
            });
        submitInProgress.value = false;
        emits('change');
        onClose();
      } catch (e) {
        submitInProgress.value = false;
        pushToastMessage({
          type: 'alert',
          subtitle: t('MY_PROFILE_PAGE.SUBMIT_ERROR_TOAST.SUBTITLE'),
        });
      }
    }
  }

  function onClose() {
    resetStates();
    emits('update:isOpen', false);
  }
</script>

<template>
  <SiteActionsModal
    :is-open="isOpen"
    :title="$t(TRANSLATION_KEYS.TITLE)"
    :in-progress="isAwaitingResponse"
    @open="onModalOpened"
    @confirm="onSubmit"
    @cancel="onClose"
    @opened="inputRef?.focus()"
  >
    <MyProfileEditModalForm>
      <MpInput
        ref="inputRef"
        v-model="values.position"
        class="form__row-full"
        :test="TRANSLATION_KEYS.POSITION_LABEL"
        :label="$t(TRANSLATION_KEYS.POSITION_LABEL)"
        :error="state.position.message"
        required
      />
      <MpInput
        v-model="values.companyName"
        class="form__row-half"
        :test="TRANSLATION_KEYS.EMPLOYER_LABEL"
        :label="$t(TRANSLATION_KEYS.EMPLOYER_LABEL)"
        :error="state.companyName.message"
        required
      />
      <MpInput
        v-model="values.location"
        class="form__row-half"
        :test="TRANSLATION_KEYS.LOCATION_LABEL"
        :label="$t(TRANSLATION_KEYS.LOCATION_LABEL)"
        :error="state.location.message"
        required
      />
      <span class="form__row-half">
        <MpDatePicker
          v-model="values.startedOn"
          :label="$t(TRANSLATION_KEYS.START_DATE_LABEL)"
          :error="state.startedOn.message"
          :max-date="values.endedOn ? values.endedOn : new Date()"
          initial-pick="year"
          pick="month"
          :data-test="TRANSLATION_KEYS.START_DATE_LABEL"
          required
        />
      </span>
      <span class="form__row-half">
        <MpDatePicker
          v-model="values.endedOn"
          :label="$t(TRANSLATION_KEYS.END_DATE_LABEL)"
          :error="state.endedOn.message"
          :message="$t(TRANSLATION_KEYS.END_DATE_DESCRIPTION)"
          :min-date="values.startedOn ? values.startedOn : undefined"
          :max-date="new Date()"
          initial-pick="year"
          pick="month"
          :data-test="TRANSLATION_KEYS.END_DATE_LABEL"
          clearable
        />
      </span>
      <span class="form__row-full">
        <MpTextArea
          v-model="values.description"
          :label="$t(TRANSLATION_KEYS.DESCRIPTION_LABEL)"
          :placeholder="$t(TRANSLATION_KEYS.DESCRIPTION_PLACEHOLDER)"
          :error="state.description.message"
          rows="6"
        />
      </span>
    </MyProfileEditModalForm>
  </SiteActionsModal>
</template>
